
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDeviceFingerprint } from './useDeviceFingerprint';
import { MonitoringEvent, MonitoringEventType } from '@/types/monitoring';
import { updateIntegrityScore } from '@/utils/integrity-score';

export const useTestMonitoring = (
  testResponseId: string,
  currentQuestionId?: string
) => {
  const fingerprintData = useDeviceFingerprint();

  const logEvent = useCallback(async (event: Omit<MonitoringEvent, 'test_response_id'>) => {
    try {
      const { error } = await supabase
        .from('test_monitoring_events')
        .insert({
          test_response_id: testResponseId,
          event_type: event.event_type,
          event_data: event.event_data,
          severity: event.severity,
          duration: event.duration,
          question_id: event.question_id || currentQuestionId,
          device_fingerprint: fingerprintData?.fingerprint,
          ai_confidence_score: event.ai_confidence_score,
          created_at: event.created_at || new Date().toISOString()
        });
        
      if (error) {
        console.error('Error logging monitoring event:', error);
      }
    } catch (error) {
      console.error('Error logging monitoring event:', error);
    }
  }, [testResponseId, fingerprintData, currentQuestionId]);

  const updateIntegrityScoreCallback = useCallback(async () => {
    try {
      // First, get all monitoring events for this test response
      const { data: events, error: eventsError } = await supabase
        .from('test_monitoring_events')
        .select('*')
        .eq('test_response_id', testResponseId);
      
      if (eventsError) {
        console.error('Error fetching monitoring events:', eventsError);
        return;
      }
      
      // Filter events to only include valid types as defined in the MonitoringEventType
      const validEventTypes: MonitoringEventType[] = [
        'tab_switch',
        'window_blur',
        'timing_anomaly',
        'ai_content',
        'content_similarity',
        'device_location',
        'clipboard_action'
      ];

      const validEvents = events.filter(event => 
        validEventTypes.includes(event.event_type as MonitoringEventType)
      ) as MonitoringEvent[];
      
      // Use our standardized function to update the integrity score
      await updateIntegrityScore(testResponseId, validEvents);
    } catch (error) {
      console.error('Error updating integrity score:', error);
    }
  }, [testResponseId]);

  return {
    logEvent,
    updateIntegrityScore: updateIntegrityScoreCallback
  };
};
